import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import { omit } from 'lodash';
import { DotBounceLoading } from 'mintel-react-ui';

import { ApiResponse, ContentLocation } from '/types';
import {
  MetadataContext,
  PublishStatusContext,
  UserAccessRestrictionContext,
} from '/utils/contexts';
import { buildArticleApiRelativeUrl, useCwaAxios } from '/utils/cwaAxios';
import FavoritesProvider from '/utils/FavoritesProvider';
import { Error403, Error404, Error500 } from '/utils/HttpError';

import style from './CwaArticleGetter.scss';
import ContentPage from '../ContentPage';

// A list of URL params that if found in the URL will be passed
// with the API calls.
const URL_PARAMS_TO_PASS_TO_SERVER = ['utm_source', 'fromSearch', 'highlight'];

const CwaArticleGetter: FC<ContentLocation> = ({
  publishStatus,
  contentType,
  slugOrId,
  blockNum,
}) => {
  const [apiResponse, setApiResponse] = useState(null as ApiResponse);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState(0);
  const cwaAxios = useCwaAxios(setIsLoading);
  // We want to avoid re-fetching the same article when tokens change
  const [fetchedUrl, setFetchedUrl] = useState('');
  const accessRestrictions = useContext(UserAccessRestrictionContext);

  const urlParams = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    for (const key of urlParams.keys()) {
      if (!URL_PARAMS_TO_PASS_TO_SERVER.includes(key)) {
        urlParams.delete(key);
      }
    }
    if (accessRestrictions.isMasquerading) {
      urlParams.append('isMasquerading', '1');
    }
    return urlParams;
  }, [accessRestrictions.isMasquerading]);

  useEffect(() => {
    const articleUrl = buildArticleApiRelativeUrl({
      publishStatus,
      contentType,
      slugOrId,
    });
    if (articleUrl !== fetchedUrl) {
      const urlParamsString = urlParams.toString();

      cwaAxios
        .get(urlParamsString ? `${articleUrl}?${urlParamsString}` : articleUrl)
        .then((resp) => {
          setApiResponse(resp.data);
          setFetchedUrl(articleUrl);
        })
        .catch((error) => {
          setErrorCode(error?.response?.status || 500);
        });
    }
  }, [
    fetchedUrl,
    urlParams,
    cwaAxios,
    publishStatus,
    contentType,
    slugOrId,
    setApiResponse,
  ]);

  if (errorCode === 403) {
    return <Error403 />;
  }

  if (errorCode === 404) {
    return <Error404 />;
  }

  if (errorCode) {
    return <Error500 />;
  }

  if (!isLoading && apiResponse) {
    return (
      <MetadataContext.Provider value={omit(apiResponse, ['content'])}>
        <PublishStatusContext.Provider value={publishStatus}>
          <FavoritesProvider>
            <ContentPage
              content={apiResponse?.content}
              contentType={contentType}
              blockNum={blockNum}
              groupId={apiResponse?.group_id}
              articleId={apiResponse?.id}
            />
          </FavoritesProvider>
        </PublishStatusContext.Provider>
      </MetadataContext.Provider>
    );
  }

  return (
    <div className={style.loading} data-testid="page-loading">
      <DotBounceLoading />
    </div>
  );
};

export default CwaArticleGetter;
